import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Pencil, Download } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { FilterPills } from "@/components/FilterPills";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { downloadCSV } from "@/lib/csv";

const PLACEMENT_LABELS = {
  landing: "Homepage",
  customer_dashboard: "Customer dashboard",
  collector_dashboard: "Collector dashboard",
};

const STATUS_OPTIONS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "off", label: "Off" },
];
const PAYMENT_OPTIONS = [
  { key: "all", label: "All payments" },
  { key: "paid", label: "Paid" },
  { key: "pending", label: "Pending" },
  { key: "unpaid", label: "Unpaid" },
];

const fmt = (s) => (s ? new Date(s).toLocaleDateString() : "—");

export default function AdminAds() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [payment, setPayment] = useState("all");
  const [range, setRange] = useState({ start: "", end: "" });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ads");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const inRange = (iso) => {
    if (!range.start && !range.end) return true;
    if (!iso) return false;
    const d = iso.slice(0, 10);
    if (range.start && d < range.start) return false;
    if (range.end && d > range.end) return false;
    return true;
  };

  const filtered = useMemo(() => items.filter((ad) => {
    if (status === "active" && !ad.active) return false;
    if (status === "off" && ad.active) return false;
    const pay = ad.payment_status || "unpaid";
    if (payment === "paid" && pay !== "paid") return false;
    if (payment === "pending" && pay !== "pending") return false;
    if (payment === "unpaid" && (pay === "paid" || pay === "pending")) return false;
    if (!inRange(ad.created_at)) return false;
    return true;
  }), [items, status, payment, range]);

  const exportCSV = () => {
    if (!filtered.length) { toast.error("Nothing to export"); return; }
    const rows = filtered.map((ad) => ({
      title: ad.title || "",
      headline: ad.headline || "",
      placements: (ad.placements || []).map((p) => PLACEMENT_LABELS[p] || p).join(" | "),
      start: fmt(ad.start_at),
      end: fmt(ad.end_at),
      active: ad.active ? "Active" : "Off",
      payment_status: ad.payment_status === "paid" ? "Paid" : ad.payment_status === "pending" ? "Pending" : "Unpaid",
      price: ad.price ? Number(ad.price).toFixed(2) : "0.00",
      impressions: ad.impressions || 0,
      clicks: ad.clicks || 0,
    }));
    downloadCSV("ads.csv", rows);
    toast.success("CSV downloaded");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this ad?")) return;
    try {
      await api.delete(`/admin/ads/${id}`);
      toast.success("Ad deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="container-x py-12" data-testid="admin-ads">
      <AdminTabs />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-[#2D6A4F]" /> Advertisements ({filtered.length})
          </h1>
          <p className="text-[#5C6B64] mt-2">Create sponsored placements shown on the homepage and dashboards, with scheduling and ZIP/service targeting.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="admin-ads-export-csv" onClick={exportCSV} disabled={!filtered.length} className="btn-secondary inline-flex items-center gap-2 text-sm py-2 px-3 disabled:opacity-50"><Download className="w-4 h-4" /> Export CSV</button>
          <button data-testid="admin-ad-new" onClick={() => navigate("/admin/ads/new")} className="btn-primary inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New ad</button>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-2">
        <FilterPills options={STATUS_OPTIONS} value={status} onChange={setStatus} testidPrefix="admin-ads-status-filter" />
        <FilterPills options={PAYMENT_OPTIONS} value={payment} onChange={setPayment} testidPrefix="admin-ads-payment-filter" />
        <DateRangeFilter onChange={(start, end) => setRange({ start, end })} />
      </div>

      {loading ? (
        <div className="mt-10 text-[#5C6B64]">Loading…</div>
      ) : filtered.length === 0 ? (
        <p className="mt-8 text-sm text-[#8A9A86]">{items.length === 0 ? "No ads yet. Create your first sponsored placement." : "No ads match these filters."}</p>
      ) : (
        <div className="mt-8 overflow-x-auto rounded-xl border border-[#1E3F33]/10">
          <table className="w-full text-sm" data-testid="admin-ads-table">
            <thead className="bg-[#F0EFEA] text-left text-[#1E3F33]"><tr>
              <th className="px-4 py-2.5 font-medium">Ad</th>
              <th className="px-4 py-2.5 font-medium">Placements</th>
              <th className="px-4 py-2.5 font-medium">Schedule</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Perf.</th>
              <th className="px-4 py-2.5 font-medium"></th>
            </tr></thead>
            <tbody>
              {filtered.map((ad) => (
                <tr key={ad.id} className="border-t border-[#1E3F33]/8" data-testid={`admin-ad-row-${ad.id}`}>
                  <td className="px-4 py-2.5"><div className="font-medium text-[#1E3F33]">{ad.title}</div><div className="text-xs text-[#8A9A86] truncate max-w-[200px]">{ad.headline}</div></td>
                  <td className="px-4 py-2.5 text-[#5C6B64] text-xs">{(ad.placements || []).map((p) => PLACEMENT_LABELS[p] || p).join(", ") || "—"}</td>
                  <td className="px-4 py-2.5 text-[#5C6B64] text-xs">{fmt(ad.start_at)} → {fmt(ad.end_at)}</td>
                  <td className="px-4 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full ${ad.active ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : "bg-[#1E3F33]/10 text-[#5C6B64]"}`}>{ad.active ? "Active" : "Off"}</span>
                    <span className={`ml-1 text-[10px] px-2 py-0.5 rounded-full ${ad.payment_status === "paid" ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : ad.payment_status === "pending" ? "bg-[#CC5A3A]/15 text-[#CC5A3A]" : "bg-[#1E3F33]/10 text-[#5C6B64]"}`}>{ad.payment_status === "paid" ? "Paid" : ad.payment_status === "pending" ? "Pending" : "Unpaid"}</span>
                  </td>
                  <td className="px-4 py-2.5 text-[#5C6B64] text-xs">{ad.price ? `$${Number(ad.price).toFixed(2)}` : "—"} · {ad.impressions || 0}v/{ad.clicks || 0}c</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`admin-ad-edit-${ad.id}`} onClick={() => navigate(`/admin/ads/${ad.id}`)} className="btn-secondary text-xs py-1.5 px-3 mr-1 inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                    <button data-testid={`admin-ad-delete-${ad.id}`} onClick={() => remove(ad.id)} className="text-[#E63946] hover:bg-[#E63946]/10 rounded-lg p-1.5 inline-flex"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
